import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertComponent } from 'src/app/common/alert/alert.component';
import { NotificationSubscription } from '../../models/notification-subscription.model';
import { adminService } from 'src/app/services/admin.service';
import { GlobalService } from 'src/app/services/global.service';
import { RefineryService } from 'src/app/services/refinery.service';
import { Tenant } from 'src/app/models/tenant.model';
import { NotificationSubscriptionCategory } from 'src/app/models/interfaces/notificationSubscriptionCategory';
import { ReportScheduler } from 'src/app/models/interfaces/reportScheduler';

@Component({
  selector: 'app-notification-page',
  templateUrl: './notification-page.component.html',
  styleUrls: ['./notification-page.component.css']
})
export class NotificationPageComponent implements OnInit {
  isLoading = false;
  tenantList: Tenant[];
  categoryList: NotificationSubscriptionCategory[];
  schedulerList: ReportScheduler[];

  public notificationDetailForm = new UntypedFormGroup({    
    tenantId: new UntypedFormControl({ value: '', disabled: false }, Validators.required),
    notificationCategoryId: new UntypedFormControl({ value: '', disabled: false }, Validators.required),
    channelName: new UntypedFormControl({ value: '', disabled: false }),
    webHookUrl: new UntypedFormControl({ value: '', disabled: false }),    
    successNotification: new UntypedFormControl({ value: false, disabled: false }),
    failureNotification: new UntypedFormControl({ value: false, disabled: false }),
    reportScheduleId: new UntypedFormControl({ value: '', disabled: false }) 
  });
  constructor(public globalService: GlobalService, public dialogRef: MatDialogRef<NotificationPageComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private readonly adminService: adminService, private readonly refineryService: RefineryService) { }

  ngOnInit(): void {
    this.loadTenantList();
    this.loadNotificationCategoryList();
    this.loadReportScheduler();
    this.loadNotificationPageData();
  }

  loadNotificationPageData() {
    this.isLoading = true;
    if (this.globalService.isNotNullObject(this.data.data) && this.data.displayType !== 'add') {
      let disableMode = false;
      let isTenantDisabled = false;
      if (this.data.displayType === 'view') {
        disableMode = true; 
        isTenantDisabled = true;       
      }
      if (this.data.displayType === 'edit') {
        isTenantDisabled = true;
      }            
      this.notificationDetailForm = new UntypedFormGroup({    
        tenantId: new UntypedFormControl({ value: this.data.data.tenantId, disabled: isTenantDisabled }, Validators.required),
        notificationCategoryId: new UntypedFormControl({ value: this.data.data.notificationCategoryId, disabled: isTenantDisabled }, Validators.required),
        channelName: new UntypedFormControl({ value: this.data.data.channelName, disabled: disableMode }),
        webHookUrl: new UntypedFormControl({ value: this.data.data.webhookUrl, disabled: disableMode }),
        successNotification: new UntypedFormControl({ value: this.data.data.successNotification, disabled: disableMode }),
        failureNotification: new UntypedFormControl({ value: this.data.data.failedNotification, disabled: disableMode }),
        reportScheduleId: new UntypedFormControl({ value: this.data.data.reportScheduleId, disabled: disableMode }) 
      });
      this.isLoading = false;
    } else {
      this.isLoading = false;
    }
  }

  /**Load Tenant List data for selected refinery*/
  loadTenantList() {
    this.adminService.getTenantList(this.refineryService.currentRefinery.refineryId).subscribe((data: any): void => {
      this.tenantList = data;
      
    }, (error: Error) => {
      console.log('Error while fetching tenant list', error.message);
    });
  }

  loadReportScheduler() {
    this.adminService.getReportScheduler().subscribe((data: ReportScheduler[]): void => {
        this.schedulerList = data;        
      }, (error: Error) => {
        console.log('Error while fetching report scheduler list', error.message);
      });
  }

  loadNotificationCategoryList() {
    this.adminService.getNotificationCategory().subscribe((data: NotificationSubscriptionCategory[]): void => {
        this.categoryList = data;
        
      }, (error: Error) => {
        console.log('Error while fetching notification category list', error.message);
      });
  }

  /**Cancels the pop-up */
  onCancelClick(value: boolean): void {
    this.dialogRef.close(value);
  }

  disableSave() {
    const notificationCategoryId = this.notificationDetailForm.get('notificationCategoryId').value;
    this.notificationDetailForm.get('channelName').setValidators([Validators.required]);
    this.notificationDetailForm.get('webHookUrl').setValidators([Validators.required, Validators.pattern('https://([da-z.-]+).([a-z.]{2,6})([/w .-]*)??([^#\n\r]*)#?([^#\n\r]*)/?')]);      
    if (notificationCategoryId === 1) {            
      this.notificationDetailForm.get('reportScheduleId').clearValidators(); 
      this.notificationDetailForm.get('reportScheduleId').updateValueAndValidity();
    }
    else if (notificationCategoryId !== 1) {      
      this.notificationDetailForm.get('reportScheduleId').setValidators([Validators.required]);
    }
    
    const check: boolean = !this.notificationDetailForm.valid;
    return check;
  }  

  saveSubscription() {
    const notificationData: NotificationSubscription = this.notificationDetailForm.value;
    notificationData.tenantId = this.notificationDetailForm.controls.tenantId.value;
    notificationData.notificationCategoryId = this.notificationDetailForm.controls.notificationCategoryId.value;
    notificationData.notificationCategoryName = this.categoryList.find((item: NotificationSubscriptionCategory) => item.notificationCategoryId === notificationData.notificationCategoryId).categoryName;
    
    notificationData.notificationSubscriptionId = this.globalService.isNotNullObject(this.data.data) && this.data.displayType !== 'add' ? this.data.data.notificationSubscriptionId : 0;
    if(notificationData.notificationCategoryId === 1) {
      notificationData.reportScheduleId = 0;
    }    
    notificationData.emailDLName = '';
    notificationData.subscribedBy = this.globalService.loggedInUser;
        
    this.adminService.saveNotificationSubscription(notificationData, this.data.displayType).subscribe((data: any): void => {
      this.globalService.showAlert(AlertComponent, { success: true, message: 'Notification subscription saved successfully.' });
      this.onCancelClick(true);
    }, (error: Error) => {
      this.isLoading = false;
      this.globalService.showAlert(AlertComponent, { success: false, message: 'Notification subscription could not be saved.' });
    });
  }

}
